import { memo } from 'react';
import { Users, Eye, Search, Award, TrendingUp, Linkedin } from 'lucide-react';

export const LinkedInDeepdive = memo(({ data, loading, error }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-semibold">Error loading LinkedIn data</p>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center text-gray-600">
        No data available
      </div>
    );
  }

  const { metrics, changes, ssiBreakdown } = data;

  const MetricCard = ({ label, value, change, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 border-blue-200 text-blue-700',
      green: 'bg-green-50 border-green-200 text-green-700',
      purple: 'bg-purple-50 border-purple-200 text-purple-700',
      orange: 'bg-orange-50 border-orange-200 text-orange-700',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    };

    const iconColorClasses = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      purple: 'bg-purple-100 text-purple-600',
      orange: 'bg-orange-100 text-orange-600',
      indigo: 'bg-indigo-100 text-indigo-600',
    };

    return (
      <div className={`p-6 rounded-lg border ${colorClasses[color]}`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{label}</p>
            <p className={`text-3xl font-bold mt-2 ${colorClasses[color].split(' ')[2]}`}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {change !== null && change !== undefined && (
              <div className="mt-2 flex items-center text-sm">
                <span className={`font-semibold ${
                  change > 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {change > 0 ? '↑' : '↓'} {Math.abs(change).toFixed(1)}%
                </span>
                <span className="text-gray-500 ml-1">vs last period</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-lg ${iconColorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </div>
    );
  };

  const getSSILevel = (score) => {
    if (score >= 75) return { label: 'Elite', color: 'text-green-600', bgColor: 'bg-green-100' };
    if (score >= 50) return { label: 'Good', color: 'text-blue-600', bgColor: 'bg-blue-100' };
    if (score >= 25) return { label: 'Fair', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    return { label: 'Needs Work', color: 'text-red-600', bgColor: 'bg-red-100' };
  };

  const ssiLevel = getSSILevel(metrics.ssi);

  return (
    <div className="space-y-6">
      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard
          label="Connections"
          value={metrics.connections}
          change={changes.connections}
          icon={Users}
          color="blue"
        />
        <MetricCard
          label="Followers"
          value={metrics.followers}
          change={changes.followers}
          icon={Linkedin}
          color="indigo"
        />
        <MetricCard
          label="Profile Views"
          value={metrics.profileViews}
          change={changes.profileViews}
          icon={Eye}
          color="green"
        />
        <MetricCard
          label="Searches"
          value={metrics.searches}
          change={changes.searches}
          icon={Search}
          color="purple"
        />
        <MetricCard
          label="SSI Score"
          value={metrics.ssi}
          change={changes.ssi}
          icon={Award}
          color="orange"
        />
      </div>

      {/* SSI Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900">Social Selling Index (SSI) Breakdown</h4>
              <p className="text-sm text-gray-600 mt-1">Four pillars of social selling on LinkedIn</p>
            </div>
            <div className={`px-4 py-2 rounded-full ${ssiLevel.bgColor}`}>
              <span className={`text-sm font-semibold ${ssiLevel.color}`}>
                {metrics.ssi}/100 - {ssiLevel.label}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Establish Brand */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Establish Your Brand</p>
                <span className="text-sm font-semibold text-gray-900">
                  {ssiBreakdown.establishBrand}/25
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full transition-all"
                  style={{ width: `${(ssiBreakdown.establishBrand / 25) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Find Right People */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Find the Right People</p>
                <span className="text-sm font-semibold text-gray-900">
                  {ssiBreakdown.findRightPeople}/25
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-500 h-3 rounded-full transition-all"
                  style={{ width: `${(ssiBreakdown.findRightPeople / 25) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Engage with Insights */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Engage with Insights</p>
                <span className="text-sm font-semibold text-gray-900">
                  {ssiBreakdown.engageInsights}/25
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-purple-500 h-3 rounded-full transition-all"
                  style={{ width: `${(ssiBreakdown.engageInsights / 25) * 100}%` }}
                ></div>
              </div>
            </div>

            {/* Build Relationships */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">Build Relationships</p>
                <span className="text-sm font-semibold text-gray-900">
                  {ssiBreakdown.buildRelationships}/25
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-orange-500 h-3 rounded-full transition-all"
                  style={{ width: `${(ssiBreakdown.buildRelationships / 25) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Summary */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          LinkedIn Performance Summary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Network Growth:</span>{' '}
              Your network has grown by {changes.connections.toFixed(1)}% with{' '}
              {metrics.connections.toLocaleString()} total connections
            </p>
          </div>
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Visibility:</span>{' '}
              {metrics.profileViews.toLocaleString()} profile views and{' '}
              {metrics.searches.toLocaleString()} search appearances
            </p>
          </div>
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">Follower Base:</span>{' '}
              {metrics.followers.toLocaleString()} followers, up {changes.followers.toFixed(1)}%
            </p>
          </div>
          <div>
            <p className="text-gray-700">
              <span className="font-semibold">SSI Strength:</span>{' '}
              Your SSI score of {metrics.ssi} puts you in the "{ssiLevel.label}" category
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

LinkedInDeepdive.displayName = 'LinkedInDeepdive';